import { useState, useMemo } from 'react';
import { View, Pressable, ScrollView, Text } from 'react-native';
import SheetModal from '@/components/General/SheetModal';
import MarkdownText from '@/components/General/MarkdownText';
import { releaseNotes } from '@/assets/release-notes';
import { StyleSheet } from 'react-native-unistyles';

const ReleaseNotesModal = ({ isOpen, onClose }) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    const sortedNotes = useMemo(() => releaseNotes.slice(), []);
    const hasNotes = sortedNotes.length > 0;
    const currentNote = sortedNotes[selectedIndex] || null;

    return (
        <SheetModal
            title="What's New"
            isOpen={isOpen}
            onCancel={onClose}
            cancelText="Close"
        >
            <View style={styles.container}>
                {hasNotes ? (
                    <>
                        {/* Version selector */}
                        {sortedNotes.length > 1 && (
                            <ScrollView
                                horizontal
                                showsHorizontalScrollIndicator={false}
                                style={styles.selector}
                                contentContainerStyle={styles.selectorContent}
                                keyboardShouldPersistTaps="handled"
                            >
                                {sortedNotes.map((note, idx) => (
                                    <Pressable
                                        key={idx}
                                        testID={`release-note-tab-${idx}`}
                                        onPress={() => setSelectedIndex(idx)}
                                        style={styles.tab(idx === selectedIndex)}
                                    >
                                        <Text style={styles.tabText(idx === selectedIndex)}>
                                            {note.version || note.date}
                                        </Text>
                                        {idx === 0 && (
                                            <View style={styles.newPill}>
                                                <Text style={styles.newPillText}>Latest</Text>
                                            </View>
                                        )}
                                    </Pressable>
                                ))}
                            </ScrollView>
                        )}

                        <ScrollView style={styles.scroll} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled"
>
                            {currentNote?.date && (
                                <Text style={styles.date}>{currentNote.date}</Text>
                            )}
                            <MarkdownText source={currentNote?.content || ''} />
                        </ScrollView>
                    </>
                ) : (
                    <View style={styles.empty}>
                        <Text style={styles.emptyText}>No release notes yet. Check back soon!</Text>
                    </View>
                )}
            </View>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, roundness }) => ({
    container: {
        flex: 1,
        minHeight: 300,
    },
    selector: {
        maxHeight: 60,
        marginBottom: spacing.sm,
    },
    selectorContent: {
        gap: spacing.sm,
        paddingHorizontal: spacing.xs,
    },
    tab: (isActive) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.md,
        borderRadius: roundness.md,
        backgroundColor: isActive ? colors.primaryContainer : colors.surfaceVariant,
        borderWidth: 1,
        borderColor: isActive ? colors.primary : colors.borderSubtle,
    }),
    tabText: (isActive) => ({
        fontFamily: isActive ? fonts.bold : fonts.body,
        fontSize: typography.caption,
        color: isActive ? colors.primary : colors.textTertiary,
    }),
    newPill: {
        backgroundColor: colors.primary,
        borderRadius: 10,
        paddingHorizontal: 6,
        paddingVertical: 2,
    },
    newPillText: {
        fontFamily: fonts.bold,
        fontSize: typography.micro,
        color: colors.onPrimary,
    },
    scroll: {
        flex: 1,
    },
    date: {
        fontFamily: fonts.heading,
        fontSize: typography.caption,
        color: colors.textTertiary,
        marginBottom: spacing.sm,
    },
    empty: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: spacing.xxl,
    },
    emptyText: {
        fontFamily: fonts.body,
        fontSize: typography.body,
        color: colors.textTertiary,
    },
}));

export default ReleaseNotesModal;
